import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AddFacility: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { token, logout } = useAuth();
  const isEditMode = Boolean(id);

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    address: '',
  });

  useEffect(() => {
    if (!isEditMode) return;
    const fetchFacility = async () => {
      try {
        setIsFetching(true);
        const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
        const response = await fetch(`${baseUrl}/api/v1/facilities/${id}`, {
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });

        if (response.status === 401) {
          logout();
          return;
        }

        const data = await response.json();
        if (!response.ok || !data.success) {
          throw new Error(data.message || 'Failed to fetch facility');
        }

        const f = data.data;
        setFormData({
          name: f.name || '',
          code: f.code || '',
          address: f.address || '',
        });
      } catch (err: any) {
        setError(err.message || 'Failed to load facility details.');
      } finally {
        setIsFetching(false);
      }
    };

    fetchFacility();
  }, [id, token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id: fieldId, value } = e.target;
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setIsLoading(true);
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000';
      const url = isEditMode
        ? `${baseUrl}/api/v1/facilities/${id}`
        : `${baseUrl}/api/v1/facilities`;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          name: formData.name,
          code: formData.code,
          address: formData.address,
        }),
      });

      if (response.status === 401) {
        logout();
        return;
      }

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || `Failed to ${isEditMode ? 'update' : 'create'} facility`);
      }

      navigate('/facility-management');
    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-slate-400">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#136bce] border-t-transparent"></div>
          <p className="text-sm">Loading facility details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full animate-in fade-in duration-500">
      <div className="mb-6">
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-1 font-medium">
          <Link to="/facility-management" className="hover:text-[#136bce]">Facility Management</Link>
          <span className="material-symbols-outlined text-[14px]">chevron_right</span>
          <span className="text-[#136bce]">{isEditMode ? 'Edit Facility' : 'Add Facility'}</span>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">
          {isEditMode ? 'Edit Facility' : 'Add New Facility'}
        </h1>
        <p className="text-sm text-slate-500">
          {isEditMode
            ? 'Update the facility details below.'
            : 'Enter the details below to add a new production facility.'}
        </p>
      </div>

      {error && (
        <div className="mb-6 rounded-lg bg-red-50 p-4 border border-red-200 flex items-center gap-3 text-red-700 animate-in slide-in-from-top-2">
          <span className="material-symbols-outlined text-[20px]">error</span>
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}

      <div className="mx-auto max-w-2xl pb-12">
        <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-[#136bce] ring-4 ring-blue-50/50">
              <span className="material-symbols-outlined text-[28px]">factory</span>
            </div>
            <div>
              <p className="text-base font-semibold text-slate-800">
                {isEditMode ? 'Edit Facility Details' : 'New Facility'}
              </p>
              <p className="text-xs text-slate-500">All fields marked with * are required</p>
            </div>
          </div>

          <div className="h-px bg-slate-100 mb-6"></div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-2">
              <div className="col-span-2 md:col-span-1">
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="name">
                  Facility Name <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded-lg border-slate-200 py-2.5 text-sm text-slate-700 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] transition-shadow"
                  id="name"
                  placeholder="e.g. Main Production Unit"
                  type="text"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-span-2 md:col-span-1">
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="code">
                  Facility Code <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded-lg border-slate-200 py-2.5 font-mono text-sm text-slate-700 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] transition-shadow"
                  id="code"
                  placeholder="e.g. FAC-001"
                  type="text"
                  required
                  value={formData.code}
                  onChange={handleInputChange}
                />
              </div>

              <div className="col-span-2">
                <label className="mb-2 block text-sm font-medium text-slate-700" htmlFor="address">
                  Address
                </label>
                <div className="relative">
                  <div className="pointer-events-none absolute left-3 top-3">
                    <span className="material-symbols-outlined text-[18px] text-slate-400">location_on</span>
                  </div>
                  <textarea
                    className="w-full rounded-lg border-slate-200 py-2.5 pl-10 text-sm text-slate-700 placeholder-slate-400 focus:border-[#136bce] focus:ring-[#136bce] transition-shadow resize-none"
                    id="address"
                    placeholder="e.g. 12 Industrial Zone, Dhaka, Bangladesh"
                    rows={3}
                    value={formData.address}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center justify-end gap-3 border-t border-slate-100 pt-6">
              <button
                type="button"
                onClick={() => navigate('/facility-management')}
                className="rounded-lg border border-slate-200 bg-white px-6 py-2.5 text-sm font-bold text-slate-600 transition-all hover:bg-slate-50 hover:text-slate-900 focus:outline-none"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-lg bg-[#136bce] px-8 py-2.5 text-sm font-bold text-white shadow-md shadow-blue-500/20 transition-all hover:bg-blue-700 hover:scale-[1.02] active:scale-[0.98] focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-[18px]">save</span>
                    {isEditMode ? 'Save Changes' : 'Add Facility'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddFacility;
